import { useMemo, useState } from 'react'
import { ActionIcon, Divider, Group, Paper, Skeleton, Stack, Text, Title, Tooltip } from '@mantine/core'
import { faListCheck, faPaperclip } from '@fortawesome/sharp-duotone-solid-svg-icons'
import { faClipboardList, faClock, faRotateReverse, faUsers } from '@fortawesome/sharp-duotone-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useClaimDetails } from '../../hooks/useClaimDetails'
import { useClaimAttachments } from '../../hooks/useClaimAttachments'
import { ClaimHeader } from './ClaimHeader'
import { ClaimKeyData } from './Summary/ClaimKeyData'
import { ClaimTabs } from './ClaimTabs/ClaimTabs'
import { Button } from '../Common/Button'
import { AttachmentExplorer } from '../Common/BoxAttchments/AttachmentExplorer'
import type { ClaimTabItem } from '../../types/tabs'
import type { KeyDataItem } from '../../types/keyDataItem'
import type { ContactInfo } from '@api-sdk'
import type { Attachment } from '../../types/attachment'
import type { AttachmentMode } from '../../hooks/useClaimAttachments'
import { formatDate, formatCurrency } from '../../utils/formatters'
import { isComposeMode, getHasEmailAttachments } from '../../utils/officeHelpers'

const getAttachmentTooltipLabel = (hasClaimFolder: boolean, hasEmailAttachments: boolean): string => {
  if (hasClaimFolder && hasEmailAttachments) return 'View documents in Box'
  if (hasClaimFolder) return 'No email attachments to save'
  return 'No Box folder linked to this claim'
}

interface AttachmentExplorerConfig {
  attachments: Attachment[]
  title: string
  primarySectionTitle: string | undefined
  secondaryAttachments: Attachment[] | undefined
  secondarySectionTitle: string | undefined
  primaryActionLabel: string
}

function getAttachmentExplorerConfig(
  mode: AttachmentMode,
  emailAttachments: Attachment[],
  boxAttachments: Attachment[]
): AttachmentExplorerConfig {
  if (mode === 'save') {
    return {
      attachments: emailAttachments,
      title: 'Save Attachments',
      primarySectionTitle: 'Mail Attachments',
      secondaryAttachments: boxAttachments,
      secondarySectionTitle: 'Claim Files',
      primaryActionLabel: 'Save to Claim',
    }
  }
  return {
    attachments: boxAttachments,
    title: 'Add Attachments',
    primarySectionTitle: undefined,
    secondaryAttachments: undefined,
    secondarySectionTitle: undefined,
    primaryActionLabel: 'Add to Email',
  }
}

interface StakeholderSectionProps {
  title: string
  name?: string | null
  contact?: ContactInfo | null
  referenceNumbers?: string[] | null
}

function StakeholderSection({ title, name, contact, referenceNumbers }: Readonly<StakeholderSectionProps>) {
  const hasRefs = referenceNumbers != null && referenceNumbers.length > 0
  const hasContact = contact != null && Boolean(contact.phone || contact.email || contact.address)

  return (
    <Paper withBorder p="xs" shadow="xs">
      <Stack gap={4}>
        <Title order={6}>{title}</Title>
        {name ? (
          <Text fz="xs">{name}</Text>
        ) : (
          <Text fz="xs" c="dimmed" fs="italic">
            No Record
          </Text>
        )}
        {hasRefs && (
          <Text fz="xs" c="dimmed">
            Ref: {referenceNumbers.join(', ')}
          </Text>
        )}
        {hasContact && (
          <Stack gap={2}>
            {contact.phone && (
              <Text fz="xs" c="dimmed">
                Phone: {contact.phone}
              </Text>
            )}
            {contact.email && (
              <Text fz="xs" c="dimmed">
                Email: {contact.email}
              </Text>
            )}
            {contact.address && (
              <Text fz="xs" c="dimmed">
                Address: {contact.address}
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}

export interface ClaimDetailsPageProps {
  claimNumber: string
  onBack: () => void
}

export function ClaimDetailsPage({ claimNumber, onBack }: Readonly<ClaimDetailsPageProps>) {
  const { claim, folderId, lastUpdated, retry } = useClaimDetails(claimNumber)
  const [activeTab, setActiveTab] = useState('summary')
  const [attachmentOpen, setAttachmentOpen] = useState(false)

  const loading = claim.loading
  const error = loading ? null : claim.error

  const mode = useMemo(() => (isComposeMode() ? 'add' : 'save'), [])
  const hasEmailAttachments = useMemo(() => getHasEmailAttachments(mode), [mode])
  const {
    emailAttachments,
    boxAttachments,
    loading: attachmentsLoading,
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    navigateTo,
    navigateBack,
    canGoBack,
    navigationPath,
    progress,
    actionError,
    successMessage,
    executeAction,
    cancelAction,
  } = useClaimAttachments(claimNumber, folderId, mode, attachmentOpen)

  if (loading) {
    return (
      <Stack gap="xs" p="xs">
        <Skeleton height={16} width="40%" />
        <Skeleton height={12} width="80%" />
        <Skeleton height={12} width="60%" />
        <Skeleton height={32} width="50%" mt="xs" />
        <Stack gap={4} mt="md">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Skeleton key={i} height={40} />
          ))}
        </Stack>
      </Stack>
    )
  }

  if (error || !claim.data) {
    return (
      <Stack gap="md" p="md" align="center" justify="center" mih={300}>
        <Text fz="sm" c="red" ta="center">
          {error || 'Failed to load claim details.'}
        </Text>
        <Group gap="xs">
          <Button label="Back" size="xs" variant="outline" onClick={onBack} />
          <Button label="Retry" size="xs" variant="filled" onClick={retry} />
        </Group>
      </Stack>
    )
  }

  const claimData = claim.data
  const hasClaimFolder = Boolean(folderId)
  const explorerConfig = getAttachmentExplorerConfig(mode, emailAttachments, boxAttachments)

  const keyDataItems: KeyDataItem[] = [
    { label: 'Present Position', value: claimData.presentPosition ?? null },
    { label: 'Date of Loss', value: formatDate(claimData.dateOfLoss) },
    { label: 'Broker', value: claimData.broker?.name ?? null },
    { label: 'Policy Number', value: claimData.policyNumber ?? null },
    { label: 'Customer Ref', value: claimData.customerRef ?? null },
    { label: 'Instruction', value: formatDate(claimData.instructedDate) },
    { label: 'First Contact', value: formatDate(claimData.firstContactDate) },
    { label: 'Site Visit', value: formatDate(claimData.siteVisitDate) },
    { label: 'First Report', value: formatDate(claimData.firstReportDate) },
    { label: 'Total Reserve', value: formatCurrency(claimData.totalReserve, claimData.currency ?? undefined) },
    { label: 'Outstanding', value: formatCurrency(claimData.outstanding, claimData.currency ?? undefined) },
  ]

  const tabs: ClaimTabItem[] = [
    {
      value: 'summary',
      label: 'Summary',
      icon: faClipboardList,
      panel: <ClaimKeyData items={keyDataItems} />,
    },
    {
      value: 'contacts',
      label: 'Contacts',
      icon: faUsers,
      panel: (
        <Stack gap="md">
          <StakeholderSection
            title="Primary Client"
            name={claimData.primaryClient?.name}
            contact={claimData.primaryClient?.contactInformation}
            referenceNumbers={claimData.primaryClient?.referenceNumbers}
          />
          <Divider />
          <StakeholderSection title="Insured Contact" name={claimData.insuredContact} />
          <Divider />
          <StakeholderSection
            title="Broker"
            name={claimData.broker?.name}
            contact={claimData.broker?.contactInformation}
          />
        </Stack>
      ),
    },
    {
      value: 'tasks',
      label: 'Tasks',
      icon: faListCheck,
      panel: (
        <Text fz="xs" c="dimmed">
          Tasks Panel Content
        </Text>
      ),
    },
    {
      value: 'time-entry',
      label: 'Time Entry',
      icon: faClock,
      panel: (
        <Text fz="xs" c="dimmed">
          Time Entry Panel Content
        </Text>
      ),
    },
  ]

  return (
    <Stack
      gap="xs"
      p="xs"
      style={{ height: attachmentOpen ? '100vh' : undefined, overflow: attachmentOpen ? 'hidden' : undefined }}
    >
      <ClaimHeader
        title={claimData.lossLocation || claimData.insuredContact || claimData.primaryClient?.name || claimNumber}
        claimNumber={claimNumber}
        insuredName={claimData.insuredContact || 'N/A'}
        insurerName={claimData.primaryClient?.name || 'N/A'}
        dateOfLoss={formatDate(claimData.dateOfLoss) ?? 'N/A'}
      />
      <Group justify="flex-end" gap={4}>
        <ActionIcon variant="subtle" size="xs" aria-label="Refresh" onClick={retry}>
          <FontAwesomeIcon icon={faRotateReverse} />
        </ActionIcon>
        {lastUpdated && (
          <Text fz="xs" c="text.4">
            Updated {lastUpdated.toLocaleTimeString()}
          </Text>
        )}
      </Group>
      <Stack gap={4} align="flex-end" w="fit-content" style={{ marginLeft: 'auto' }}>
        <Button label="Connected" variant="filled" color="success.6" size="xs" fullWidth onClick={onBack} />
        <Tooltip label={getAttachmentTooltipLabel(hasClaimFolder, hasEmailAttachments)} position="bottom" withArrow>
          <Button
            label={explorerConfig.title}
            variant="filled"
            size="xs"
            fullWidth
            disabled={!hasClaimFolder || !hasEmailAttachments}
            rightSection={<FontAwesomeIcon icon={faPaperclip} />}
            onClick={() => setAttachmentOpen(true)}
          />
        </Tooltip>
      </Stack>
      <div
        style={{
          backgroundColor: attachmentOpen ? 'var(--attachment-panel-bg)' : 'var(--section-bg)',
          margin: 'calc(-1 * var(--mantine-spacing-xs))',
          marginTop: 0,
          padding: 'var(--mantine-spacing-xs)',
          display: 'flex',
          flexDirection: 'column',
          flex: attachmentOpen ? 1 : undefined,
          minHeight: attachmentOpen ? 0 : undefined,
          overflow: attachmentOpen ? 'auto' : undefined,
        }}
      >
        {attachmentOpen ? (
          <AttachmentExplorer
            {...explorerConfig}
            loading={attachmentsLoading}
            folderPath={[claimNumber, ...navigationPath]}
            onNavigate={navigateTo}
            onNavigateBack={navigateBack}
            canGoBack={canGoBack}
            selectable
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onSelectAll={toggleSelectAll}
            onPrimaryAction={executeAction}
            progress={progress ?? undefined}
            onCancelAction={cancelAction}
            actionError={actionError}
            successMessage={successMessage}
            onClose={() => setAttachmentOpen(false)}
          />
        ) : (
          <ClaimTabs tabs={tabs.slice(0, 1)} value={activeTab} onTabChange={(tab) => setActiveTab(tab || 'summary')} />
        )}
      </div>
    </Stack>
  )
}
